import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type MouseEvent } from 'react'
import { ApiError } from '../api/client'
import {
  deviceLicenseFormQuery,
  saveDeviceLicenses,
  softwareLicensesPageQuery,
  softwareLicensesTableQuery,
  type DeviceSoftwareLicenseRow,
} from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Software licenses pe fisa - inventoree/devices/others/software_licenses.php
 * si InventoreeDeviceApplicationsModel. Butoanele Add / Change license din
 * celula (HTML de la server, `data-target="#deviceLicenseModal"`) deschid
 * fereastra din `deviceSoftwareLicenses.php`: lista licentelor aplicatiei,
 * cu cele ramase (sau deja ale masinii), si Save -> saveDeviceSoftwareLicenses.
 */

/** Fereastra "Software Licenses" - `deviceSoftwareLicenses.php`. */
function LicenseModal({ device, app, onClose, onSaved }: { device: number; app: number; onClose(): void; onSaved(): void }) {
  const form = useQuery(deviceLicenseFormQuery(device, app))
  const [picked, setPicked] = useState<string[] | null>(null)
  const [saving, setSaving] = useState(false)
  const licenses = form.data?.licenses ?? []
  /* Se arata cele cu locuri libere, sau cele pe care masina le are deja. */
  const options = licenses
    .filter((l) => l.licenses_left > 0 || (l.licenses_left === 0 && l.devices.includes(device)))
    .map((l) => ({ value: String(l.id), label: l.type_name }))
  const current = picked ?? licenses.filter((l) => l.devices.includes(device)).map((l) => String(l.id))

  const save = () => {
    if (saving) return
    setSaving(true)
    void saveDeviceLicenses(device, app, current.map(Number))
      .then(() => {
        toast('Data saved!', 'Success')
        onSaved()
      })
      .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'Something went wrong', 'Error', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <Modal
      id="deviceLicenseModal"
      open
      onClose={onClose}
      title="Software Licenses"
      labelledBy="gridSystemModalLabel"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          {licenses.length > 0 && (
            <button type="button" className="btn btn-primary device_software_licenses" disabled={saving} onClick={save}>
              Save
            </button>
          )}
        </>
      }
    >
      <div className="panel-body">
        {form.isPending ? (
          <Spinner />
        ) : licenses.length === 0 ? (
          <div className="alert alert-danger">
            <i className="fa fa-ban"></i> No licenses available for this application!
          </div>
        ) : (
          <form className="form-horizontal fv-form fv-form-bootstrap" id="device_software_licenses" onSubmit={(e) => { e.preventDefault(); save() }}>
            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                <span className="color">Licenses</span>
              </label>
              <div className="col-sm-9">
                <SelectPicker id="device-licenses" name="licenses[]" title="Select ..." multiple size={10} options={options} value={current} onChange={setPicked} />
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}

const TABLE = 'deviceApplications'

export function DeviceSoftwareLicenses({ id }: ScreenProps) {
  const page = useQuery(softwareLicensesPageQuery(id))
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'asc' })
  const table = useQuery({
    ...softwareLicensesTableQuery(id, { start, length, orderColumn: order.column, orderDir: order.dir }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })
  const client = useQueryClient()
  const [modalApp, setModalApp] = useState<number | null>(null)

  /* Butoanele din celula vin ca HTML: un ascultator pe tabel, dupa closest(). */
  const onTableClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    const button = target.closest('button[data-target="#deviceLicenseModal"]')
    if (!(button instanceof HTMLElement)) return
    e.preventDefault()
    const app = Number(button.dataset.app)
    if (app > 0) setModalApp(app)
  }

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-software-licenses" onClick={onTableClick}>
      {modalApp !== null && (
        <LicenseModal
          device={id}
          app={modalApp}
          onClose={() => setModalApp(null)}
          onSaved={() => {
            setModalApp(null)
            void client.invalidateQueries({ queryKey: ['device', id, 'software-licenses'] })
          }}
        />
      )}
      <div className="row">
        <div className="col-md-12">
          <DetailsTable
            rows={table.data?.data ?? []}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            start={start}
            length={length}
            order={order}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            onOrder={(next) => {
              setOrder(next)
              setStart(0)
            }}
          />
        </div>
      </div>
    </div>
  )
}

const COLUMNS: { key: keyof DeviceSoftwareLicenseRow; title: string; orderable: boolean; width?: number }[] = [
  { key: 'short_name', title: 'Application Name', orderable: true },
  { key: 'total', title: 'Total Licenses', orderable: true },
  { key: 'active', title: 'Active Licenses', orderable: true },
  { key: 'device_status', title: 'Device License', orderable: true },
  { key: 'actions', title: 'Actions', orderable: false, width: 115 },
]

function DetailsTable({
  rows,
  total,
  fetching,
  error,
  start,
  length,
  order,
  onStart,
  onLength,
  onOrder,
}: {
  rows: DeviceSoftwareLicenseRow[]
  total: number
  fetching: boolean
  error: string | null
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable events-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = ['td-small a-left', col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled'].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      style={col.width ? { width: col.width } : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'asc' ? 'desc' : 'asc' })
                      }}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={5} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={5} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {COLUMNS.map((col) => (
                      <td key={col.key} className="td-small a-left" dangerouslySetInnerHTML={{ __html: col.key === 'short_name' ? row.short_name : row[col.key] }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select name={`${TABLE}_length`} className="form-control input-sm" value={length} onChange={(e) => onLength(Number(e.target.value))}>
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page > 0) onStart((page - 1) * length) }}>
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); onStart(b * length) }}>
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`}>
                <a href="#" onClick={(e) => { e.preventDefault(); if (page < pages - 1) onStart((page + 1) * length) }}>
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
